import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { FaChartPie } from "react-icons/fa";

import ReportExportButtons from "./components/ReportExportButtons";
import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";

import ProsetDataGrid from "./components/KuyumcuDataGrid";

import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";

import * as XLSX from "xlsx-js-style";

import "./css/KuyumcuMainPage.css";
import "./css/KuyumcuRaporlarPage.css";

/* (İsterseniz zaten varsa silebilirsiniz) */
import { formatEnum } from "./utils/enumIslemTurFormat";
import { formatNumberTR } from "./utils/numberFormat";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  RadioGroup,
  Radio,
  MenuItem,
  TextField,
  Typography,
  Divider,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

/* ==========================================================
   GRUP KODLARI
========================================================== */
const GRUP_KODLARI = [
  { id: 1, label: "MÜŞTERİ" },
  { id: 2, label: "ATÖLYE" },
  { id: 3, label: "ÇANTA" },
  { id: 4, label: "ORTAK" },
  { id: 7, label: "PERSONEL" },
  { id: 6, label: "BANKA" },
  { id: 5, label: "EMANET" },
  { id: 8, label: "ARŞİV" },
  { id: 9, label: "GİDERLER" },
];

/* ==========================================================
   PANEL-2: İŞLEM TİPİ CHECKBOX’LARI (resimdeki)
   - değerler backend enum stringleridir; sizde farklıysa burada değiştirirsiniz.
========================================================== */
const ISLEM_TIPI_OPTIONS = [
  { key: "ALTIN_ALINDI", label: "Altın Alındı" },
  { key: "ALTIN_VERILDI", label: "Altın Verildi" },

  { key: "NAKIT_ALINDI", label: "Nakit Alındı" },
  { key: "NAKIT_VERILDI", label: "Nakit Verildi" },

  { key: "ZIYNET_ALINDI", label: "Ziynet Alındı" },
  { key: "ZIYNET_VERILDI", label: "Ziynet Verildi" },

  // Nakit gram işlemler
  { key: "NAKIT_ISLEM_GRAM_ALINDI", label: "Nakit (Gram) Alındı" },
  { key: "NAKIT_ISLEM_GRAM_VERILDI", label: "Nakit (Gram) Verildi" },
];

/* ==========================================================
   TARİH UTIL
========================================================== */
const toStartOfDay = (yyyyMmDd) => {
  const [y, m, d] = yyyyMmDd.split("-").map((x) => Number(x));
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
  return dt;
};

const toEndOfDay = (yyyyMmDd) => {
  const [y, m, d] = yyyyMmDd.split("-").map((x) => Number(x));
  const dt = new Date(y, m - 1, d, 23, 59, 59, 999);
  return dt;
};

const todayYMD = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/* ==========================================================
   FORMAT
========================================================== */
const fmtNum = (v, fraction = 2) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("tr-TR", {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  });
};

const fmtGram = (v) => fmtNum(v, 2);

const fmtInt = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0";

  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const formatDateTimeTR = (isoLike) => {
  if (!isoLike) return "";

  const d = new Date(isoLike);

  if (Number.isNaN(d.getTime())) return "";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");

  return `${dd}.${mm}.${yy} ${hh}:${mi}`;
};

const fixMojibakeTR = (s) => {
  if (s === null || s === undefined) return "";

  const str = String(s);

  // Bozuk UTF-8 -> latin1 izleri yoksa dokunma (performans + güvenlik)
  // Örnek izler: "Ã", "Å", "Ä", "â"
  if (!/[ÃÅÄâ]/.test(str)) return str;

  try {
    // latin1 gibi gelen string'i byte array'e çevirip utf-8 decode ediyoruz
    const bytes = Uint8Array.from(str, (ch) => ch.charCodeAt(0));

    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return str;
  }
};

// XSS/bozuk HTML önlemi
const escapeHtml = (s) =>
  String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

/* ==========================================================
   PANEL-1: BAKİYE ANALİZİ (Borç/Alacak/Bakiyesiz/Bakiyeli)
========================================================== */
const getBakiyeFlags = (row) => {
  const values = [
    row.gramBakiye,
    row.tlBakiye,
    row.usdBakiye,
    row.eurBakiye,
    row.yeniAta,
    row.yeniYarim,
    row.yeniCeyrek,
    row.eskiAta,
    row.eskiYarim,
    row.eskiCeyrek,
  ].map((x) => Number(x || 0));

  const anyNonZero = values.some((n) => n !== 0);
  const anyPositive = values.some((n) => n > 0);
  const anyNegative = values.some((n) => n < 0);

  return {
    anyNonZero,
    anyPositive,
    anyNegative,
  };
};

/* ==========================================================
   RAPOR TOPLAMLARI
   - Bakiye raporu: her sayısal kolon ayrı ayrı toplanır.
   - İşlem raporu: farklı para/ölçü birimleri birbirine eklenmez;
     TL, USD, EUR, GRAM ve ADET bazında ayrı toplam tutulur.
========================================================== */
const CARI_BAKIYE_TOTAL_FIELDS = [
  "gramBakiye",
  "tlBakiye",
  "usdBakiye",
  "eurBakiye",
  "yeniAta",
  "yeniYarim",
  "yeniCeyrek",
  "eskiAta",
  "eskiYarim",
  "eskiCeyrek",
];

const ISLEM_BIRIM_ORDER = ["GRAM", "ADET", "TL", "USD", "EUR", "DİĞER"];

const sumNumericField = (rows, field) =>
  (rows || []).reduce((total, row) => {
    const value = Number(row?.[field] || 0);

    return total + (Number.isFinite(value) ? value : 0);
  }, 0);

const calculateCariBakiyeTotals = (rows) =>
  CARI_BAKIYE_TOTAL_FIELDS.reduce(
    (totals, field) => ({
      ...totals,
      [field]: sumNumericField(rows, field),
    }),
    {}
  );

const STOK_ISLEM_TOTAL_FIELDS = [
  "alinanIscilik",
  "verilenIscilik",
  "miktar",
  "adet",
  "iscilikGr",
  "kar",
];

const calculateStokIslemTotals = (rows) =>
  STOK_ISLEM_TOTAL_FIELDS.reduce(
    (totals, field) => ({
      ...totals,
      [field]: sumNumericField(rows, field),
    }),
    {}
  );

const normalizeIslemBirim = (row) => {
  const raw = String(row?.birimDisplay || row?.birim || "")
    .trim()
    .toUpperCase();

  return raw || "DİĞER";
};

const calculateCariIslemTotalsByUnit = (rows) =>
  (rows || []).reduce((totals, row) => {
    const birim = normalizeIslemBirim(row);

    const borc = Number(row?.borc || 0);

    const alacak = Number(row?.alacak || 0);

    if (!totals[birim]) {
      totals[birim] = {
        borc: 0,
        alacak: 0,
      };
    }

    totals[birim].borc += Number.isFinite(borc) ? borc : 0;

    totals[birim].alacak += Number.isFinite(alacak) ? alacak : 0;

    return totals;
  }, {});

const getOrderedIslemBirimleri = (totals) => {
  const keys = Object.keys(totals || {});

  return keys.sort((a, b) => {
    const aIndex = ISLEM_BIRIM_ORDER.indexOf(a);

    const bIndex = ISLEM_BIRIM_ORDER.indexOf(b);

    const safeA = aIndex === -1 ? ISLEM_BIRIM_ORDER.length : aIndex;

    const safeB = bIndex === -1 ? ISLEM_BIRIM_ORDER.length : bIndex;

    return safeA - safeB || a.localeCompare(b, "tr");
  });
};

const formatValueByUnit = (value, birim) => {
  const n = Number(value || 0);

  if (!Number.isFinite(n)) return "0";

  if (birim === "GRAM") {
    return formatNumberTR(n, 3);
  }

  if (birim === "ADET") {
    return formatNumberTR(n, 0);
  }

  return formatNumberTR(n, 2);
};

const formatUnitTotalsHtml = (totals, field) => {
  const units = getOrderedIslemBirimleri(totals);

  if (units.length === 0) {
    return "0";
  }

  return units
    .map(
      (unit) =>
        `<div><strong>${escapeHtml(unit)}:</strong> ${escapeHtml(
          formatValueByUnit(totals[unit]?.[field] || 0, unit)
        )}</div>`
    )
    .join("");
};

const getRaporTotalCellClassName = (params) => {
  if (!params?.row?.__totalRow) {
    return "";
  }

  return params.row.__totalRowType === "bakiye"
    ? "rapor-grid-total-cell rapor-grid-total-cell-bakiye"
    : "rapor-grid-total-cell rapor-grid-total-cell-islem";
};

const printCariBakiyeAsPdf = (rows) => {
  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Cari Hesap Bakiye Listesi</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          h1 { margin: 0 0 6px 0; font-size: 22px; }
          .sub { margin: 0 0 18px 0; font-size: 12px; color: #444; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; }
          th { background: #1976d2; color: #fff; text-align: left; }
          tr:nth-child(even) td { background: #fafafa; }
          td.num { text-align: right; white-space: nowrap; }

          tr.total-row td {
            background: linear-gradient(
              90deg,
              #fff3bf,
              #d4af37
            ) !important;

            color: #4a3500;
            font-weight: 700;
            border-top: 2px solid #b8860b;
          }

          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>

      <body>
        <h1>
          Cari Hesap Bakiye Listesi
        </h1>

        <div class="sub">
          Tarih:
          ${new Date().toLocaleString("tr-TR")}
        </div>

        <table>
          <thead>
            <tr>
              <th>Hesap Adı</th>
              <th>Bölge Kod</th>
              <th>Özel Kod</th>
              <th>Gram Bakiye</th>
              <th>TL Bakiye</th>
              <th>USD Bakiye</th>
              <th>EUR Bakiye</th>
              <th>Y. Ata</th>
              <th>Y. Yarım</th>
              <th>Y. Çeyrek</th>
              <th>E. Ata</th>
              <th>E. Yarım</th>
              <th>E. Çeyrek</th>
            </tr>
          </thead>

          <tbody>
            ${(rows || [])
              .map(
                (r) => `
              <tr>
                <td>
                  ${escapeHtml(r.hesapAdi || "")}
                </td>

                <td>
                  ${escapeHtml(r.bolgeKod || "")}
                </td>

                <td>
                  ${escapeHtml(r.ozelKod || "")}
                </td>

                <td class="num">
                  ${escapeHtml(fmtGram(r.gramBakiye || 0))}
                </td>

                <td class="num">
                  ${escapeHtml(fmtNum(r.tlBakiye || 0, 2))}
                </td>

                <td class="num">
                  ${escapeHtml(fmtNum(r.usdBakiye || 0, 2))}
                </td>

                <td class="num">
                  ${escapeHtml(fmtNum(r.eurBakiye || 0, 2))}
                </td>

                <td class="num">
                  ${escapeHtml(fmtInt(r.yeniAta || 0))}
                </td>

                <td class="num">
                  ${escapeHtml(fmtInt(r.yeniYarim || 0))}
                </td>

                <td class="num">
                  ${escapeHtml(fmtInt(r.yeniCeyrek || 0))}
                </td>

                <td class="num">
                  ${escapeHtml(fmtInt(r.eskiAta || 0))}
                </td>

                <td class="num">
                  ${escapeHtml(fmtInt(r.eskiYarim || 0))}
                </td>

                <td class="num">
                  ${escapeHtml(fmtInt(r.eskiCeyrek || 0))}
                </td>
              </tr>
            `
              )
              .join("")}

            ${(() => {
              const totals = calculateCariBakiyeTotals(rows);

              return `
                <tr class="total-row">
                  <td>TOPLAM</td>
                  <td></td>
                  <td></td>

                  <td class="num">
                    ${escapeHtml(fmtGram(totals.gramBakiye))}
                  </td>

                  <td class="num">
                    ${escapeHtml(fmtNum(totals.tlBakiye, 2))}
                  </td>

                  <td class="num">
                    ${escapeHtml(fmtNum(totals.usdBakiye, 2))}
                  </td>

                  <td class="num">
                    ${escapeHtml(fmtNum(totals.eurBakiye, 2))}
                  </td>

                  <td class="num">
                    ${escapeHtml(fmtInt(totals.yeniAta))}
                  </td>

                  <td class="num">
                    ${escapeHtml(fmtInt(totals.yeniYarim))}
                  </td>

                  <td class="num">
                    ${escapeHtml(fmtInt(totals.yeniCeyrek))}
                  </td>

                  <td class="num">
                    ${escapeHtml(fmtInt(totals.eskiAta))}
                  </td>

                  <td class="num">
                    ${escapeHtml(fmtInt(totals.eskiYarim))}
                  </td>

                  <td class="num">
                    ${escapeHtml(fmtInt(totals.eskiCeyrek))}
                  </td>
                </tr>
              `;
            })()}
          </tbody>
        </table>

        <script>
          window.onload = () => {
            setTimeout(() => {
              window.focus();
              window.print();
            }, 50);
          };
        </script>
      </body>
    </html>
  `;

  const w = window.open("about:blank", "_blank");

  if (!w) {
    alert(
      "Tarayıcı pop-up penceresini engelledi. PDF yazdırma için lütfen siteye pop-up izni verin. Şimdi aynı sekmede açılacak."
    );

    const fallback = window.open("about:blank", "_self");

    if (!fallback) {
      return;
    }

    fallback.document.open();

    fallback.document.write(html);

    fallback.document.close();

    return;
  }

  w.document.open();

  w.document.write(html);

  w.document.close();
};

const printCariIslemAsPdf = (rows) => {
  const html = `
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Cari İşlem Listesi</title>

      <style>
        body {
          font-family: Arial, sans-serif;
          padding: 24px;
        }

        h1 {
          margin: 0 0 6px 0;
          font-size: 22px;
        }

        .sub {
          margin: 0 0 18px 0;
          font-size: 12px;
          color: #444;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        th,
        td {
          border: 1px solid #ddd;
          padding: 8px;
        }

        th {
          background: #1976d2;
          color: #fff;
          text-align: left;
        }

        tr:nth-child(even) td {
          background: #fafafa;
        }

        tr.total-row td {
          background: linear-gradient(
            90deg,
            #f7df91,
            #b8860b
          ) !important;

          color: #3f2b00;
          font-weight: 700;
          border-top: 2px solid #8b6508;
          vertical-align: top;
        }

        .unit-total div {
          white-space: nowrap;
          margin-bottom: 2px;
        }

        @media print {
          body {
            padding: 0;
          }
        }
      </style>
    </head>

    <body>
      <h1>
        Cari İşlem Listesi
      </h1>

      <div class="sub">
        Tarih:
        ${new Date().toLocaleString("tr-TR")}
      </div>

      <table>
        <thead>
          <tr>
            <th>Hesap Adı</th>
            <th>Grup</th>
            <th>Bölge Kod</th>
            <th>Tarih</th>
            <th>İşlem Tür</th>
            <th>Verilen</th>
            <th>Alınan</th>
            <th>Birim</th>
            <th>Açıklama</th>
          </tr>
        </thead>

        <tbody>
          ${(rows || [])
            .map(
              (r) => `
            <tr>
              <td>
                ${escapeHtml(r.hesapAdi || "")}
              </td>

              <td>
                ${escapeHtml(r.grupKod || "")}
              </td>

              <td>
                ${escapeHtml(r.bolgeKod || "")}
              </td>

              <td>
                ${escapeHtml(formatDateTimeTR(r.tarih))}
              </td>

              <td>
                ${escapeHtml(r.islemTurText || r.islemTur || "")}
              </td>

              <td style="text-align:right;">
                ${escapeHtml(fmtNum(r.borc || 0, 2))}
              </td>

              <td style="text-align:right;">
                ${escapeHtml(fmtNum(r.alacak || 0, 2))}
              </td>

              <td>
                ${escapeHtml(r.birimDisplay || r.birim || "")}
              </td>

              <td>
                ${escapeHtml(r.aciklama || "")}
              </td>
            </tr>
          `
            )
            .join("")}

          ${(() => {
            const totals = calculateCariIslemTotalsByUnit(rows);

            return `
              <tr class="total-row">
                <td>TOPLAM</td>
                <td></td>
                <td></td>

                <td>
                  ${escapeHtml(String((rows || []).length))} kayıt
                </td>

                <td></td>

                <td
                  class="unit-total"
                  style="text-align:right;"
                >
                  ${formatUnitTotalsHtml(totals, "borc")}
                </td>

                <td
                  class="unit-total"
                  style="text-align:right;"
                >
                  ${formatUnitTotalsHtml(totals, "alacak")}
                </td>

                <td>
                  Birim bazlı
                </td>

                <td></td>
              </tr>
            `;
          })()}
        </tbody>
      </table>

      <script>
        window.onload = () => {
          setTimeout(() => {
            window.focus();
            window.print();
          }, 50);
        };
      </script>
    </body>
  </html>
  `;

  // 1) Pop-up dene
  const w = window.open("about:blank", "_blank");

  // Pop-up engellendiyse fallback: aynı sekmede yazdır
  if (!w) {
    alert(
      "Tarayıcı pop-up penceresini engelledi. PDF yazdırma için lütfen siteye pop-up izni verin. Şimdi aynı sekmede açılacak."
    );

    const fallback = window.open("about:blank", "_self");

    if (!fallback) {
      return;
    }

    fallback.document.open();

    fallback.document.write(html);

    fallback.document.close();

    return;
  }

  // 2) Açıldıysa yeni sekmeye bas
  w.document.open();

  w.document.write(html);

  w.document.close();
};

/* ==========================================================
   PANEL-1: EXCEL EXPORT
========================================================== */
const exportCariBakiyeExcel = (rows) => {
  const headers = [
    "Hesap Adı",
    "Bölge Kod",
    "Özel Kod",
    "Gram Bakiye",
    "TL Bakiye",
    "USD Bakiye",
    "EUR Bakiye",
    "Y. Ata Bakiye",
    "Y. Yrm Bakiye",
    "Y. Çyr Bakiye",
    "E. Ata Bakiye",
    "E. Yrm Bakiye",
    "E. Çyr Bakiye",
  ];

  const data = (rows || []).map((r) => [
    r.hesapAdi || "",
    r.bolgeKod || "",
    r.ozelKod || "",
    Number(r.gramBakiye || 0),
    Number(r.tlBakiye || 0),
    Number(r.usdBakiye || 0),
    Number(r.eurBakiye || 0),
    Number(r.yeniAta || 0),
    Number(r.yeniYarim || 0),
    Number(r.yeniCeyrek || 0),
    Number(r.eskiAta || 0),
    Number(r.eskiYarim || 0),
    Number(r.eskiCeyrek || 0),
  ]);

  const totals = calculateCariBakiyeTotals(rows);

  const totalRow = [
    "TOPLAM",
    "",
    "",
    totals.gramBakiye,
    totals.tlBakiye,
    totals.usdBakiye,
    totals.eurBakiye,
    totals.yeniAta,
    totals.yeniYarim,
    totals.yeniCeyrek,
    totals.eskiAta,
    totals.eskiYarim,
    totals.eskiCeyrek,
  ];

  const excelRows = [...data, totalRow];

  const ws = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);

  ws["!cols"] = [
    { wch: 30 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
  ];

  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({
      r: 0,
      c,
    });

    if (!ws[addr]) {
      continue;
    }

    ws[addr].s = {
      font: {
        bold: true,
        color: {
          rgb: "FFFFFF",
        },
      },

      fill: {
        fgColor: {
          rgb: "B8860B",
        },
      },

      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },

      border: {
        top: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        bottom: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        left: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        right: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },
      },
    };
  }

  for (let r = 1; r < excelRows.length + 1; r++) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({
        r,
        c,
      });

      if (!ws[addr]) {
        continue;
      }

      const isNumberCol = c >= 3;

      ws[addr].s = {
        alignment: {
          horizontal: isNumberCol ? "right" : "left",

          vertical: "center",
        },

        border: {
          top: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },

          bottom: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },

          left: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },

          right: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },
        },
      };

      if (isNumberCol) {
        if (c === 3) {
          ws[addr].z = "#,##0.00";
        } else if (c >= 4 && c <= 6) {
          ws[addr].z = "#,##0.00";
        } else {
          ws[addr].z = "#,##0";
        }
      }
    }
  }

  const totalRowIndex = excelRows.length;

  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({
      r: totalRowIndex,
      c,
    });

    if (!ws[addr]) {
      continue;
    }

    ws[addr].s = {
      ...(ws[addr].s || {}),

      font: {
        bold: true,
        color: {
          rgb: "4A3500",
        },
      },

      fill: {
        fgColor: {
          rgb: "F7E6B5",
        },
      },

      alignment: {
        horizontal: c >= 3 ? "right" : "center",

        vertical: "center",
      },

      border: {
        top: {
          style: "medium",
          color: {
            rgb: "B8860B",
          },
        },

        bottom: {
          style: "thin",
          color: {
            rgb: "B8860B",
          },
        },

        left: {
          style: "thin",
          color: {
            rgb: "D4AF37",
          },
        },

        right: {
          style: "thin",
          color: {
            rgb: "D4AF37",
          },
        },
      },
    };
  }

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "Cari Bakiye");

  const fileName = `Cari_Hesap_Bakiye_Listesi_${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`;

  XLSX.writeFile(wb, fileName);
};

/* ==========================================================
   PANEL-2: EXCEL EXPORT (Hesap İşlemler kolonları)
========================================================== */
const exportCariIslemExcel = (rows) => {
  const headers = [
    "Hesap Adı",
    "Bölge Kod",
    "Özel Kod",
    "Tarih",
    "Tür",
    "Verilen",
    "Alınan",
    "Birim",
    "Açıklama",
  ];

  const data = (rows || []).map((r) => [
    r.hesapAdi || "",
    r.bolgeKod || "",
    r.ozelKod || "",
    formatDateTimeTR(r.tarih),
    r.islemTurText || r.islemTur || "",
    Number(r.borc || 0),
    Number(r.alacak || 0),
    r.birimDisplay || r.birim || "",
    r.aciklama || "",
  ]);

  const totalsByUnit = calculateCariIslemTotalsByUnit(rows);

  const totalRows = getOrderedIslemBirimleri(totalsByUnit).map((unit) => [
    "TOPLAM",
    "",
    "",
    `${(rows || []).length} kayıt`,
    "",
    totalsByUnit[unit].borc,
    totalsByUnit[unit].alacak,
    unit,
    "",
  ]);

  const excelRows = [...data, ...totalRows];

  const ws = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);

  ws["!cols"] = [
    { wch: 26 },
    { wch: 12 },
    { wch: 12 },
    { wch: 18 },
    { wch: 16 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 },
    { wch: 40 },
  ];

  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({
      r: 0,
      c,
    });

    if (!ws[addr]) {
      continue;
    }

    ws[addr].s = {
      font: {
        bold: true,
        color: {
          rgb: "FFFFFF",
        },
      },

      fill: {
        fgColor: {
          rgb: "B8860B",
        },
      },

      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },

      border: {
        top: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        bottom: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        left: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        right: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },
      },
    };
  }

  for (let r = 1; r < excelRows.length + 1; r++) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({
        r,
        c,
      });

      if (!ws[addr]) {
        continue;
      }

      const numCols = [5, 6];

      const isNum = numCols.includes(c);

      ws[addr].s = {
        alignment: {
          horizontal: isNum ? "right" : "left",

          vertical: "center",
        },

        border: {
          top: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },

          bottom: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },

          left: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },

          right: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },
        },
      };

      if (isNum) {
        ws[addr].z = "#,##0.00";
      }
    }
  }

  const firstTotalRowIndex = data.length + 1;

  for (
    let r = firstTotalRowIndex;
    r < firstTotalRowIndex + totalRows.length;
    r++
  ) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({
        r,
        c,
      });

      if (!ws[addr]) {
        continue;
      }

      ws[addr].s = {
        ...(ws[addr].s || {}),

        font: {
          bold: true,
          color: {
            rgb: "3F2B00",
          },
        },

        fill: {
          fgColor: {
            rgb: "E9C76A",
          },
        },

        alignment: {
          horizontal: [5, 6].includes(c) ? "right" : "center",

          vertical: "center",
        },

        border: {
          top: {
            style: "medium",
            color: {
              rgb: "8B6508",
            },
          },

          bottom: {
            style: "thin",
            color: {
              rgb: "8B6508",
            },
          },

          left: {
            style: "thin",
            color: {
              rgb: "B8860B",
            },
          },

          right: {
            style: "thin",
            color: {
              rgb: "B8860B",
            },
          },
        },
      };

      if ([5, 6].includes(c)) {
        const unit =
          ws[
            XLSX.utils.encode_cell({
              r,
              c: 7,
            })
          ]?.v;

        ws[addr].z =
          unit === "ADET"
            ? "#,##0"
            : unit === "GRAM"
            ? "#,##0.000"
            : "#,##0.00";
      }
    }
  }

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "Cari İşlem");

  const fileName = `Cari_Islem_Listesi_${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`;

  XLSX.writeFile(wb, fileName);
};

/* ==========================================================
   PANEL-4: STOK İŞÇİLİK RAPORU EXPORT
   - Alınan işçilik değeri 0'dan büyük ALTIN_VERILDI kayıtları
   - Verilen işçilik değeri 0'dan büyük ALTIN_ALINDI kayıtları
========================================================== */
const exportStokIslemRaporExcel = (rows) => {
  const headers = [
    "Tarih",
    "Hesap Adı",
    "İşlem Türü",
    "Verilen İşçilik",
    "Alınan İşçilik",
    "Miktar",
    "Adet",
    "İşçilik Gr",
    "Kâr",
  ];

  const data = (rows || []).map((r) => [
    formatDateTimeTR(r.tarih),
    r.hesapAdi || "",
    r.islemTurLabel ||
      (r.islemTur === "ALTIN_ALINDI" ? "Altın Alındı" : "Altın Verildi"),
    Number(r.alinanIscilik || 0),
    Number(r.verilenIscilik || 0),
    Number(r.miktar || 0),
    r.adet == null ? "" : Number(r.adet),
    Number(r.iscilikGr || 0),
    Number(r.kar || 0),
  ]);

  const totals = calculateStokIslemTotals(rows);

  const totalRow = [
    "TOPLAM",
    "",
    "",
    totals.alinanIscilik,
    totals.verilenIscilik,
    totals.miktar,
    totals.adet,
    totals.iscilikGr,
    totals.kar,
  ];

  const excelRows = [...data, totalRow];

  const ws = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);

  ws["!cols"] = [
    { wch: 20 },
    { wch: 28 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 14 },
    { wch: 10 },
    { wch: 18 },
    { wch: 16 },
  ];

  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({
      r: 0,
      c,
    });

    if (!ws[addr]) {
      continue;
    }

    ws[addr].s = {
      font: {
        bold: true,
        color: {
          rgb: "FFFFFF",
        },
      },

      fill: {
        fgColor: {
          rgb: "B8860B",
        },
      },

      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },

      border: {
        top: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        bottom: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        left: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        right: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },
      },
    };
  }

  for (let r = 1; r <= excelRows.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({
        r,
        c,
      });

      if (!ws[addr]) {
        continue;
      }

      const isNumeric = c >= 3;

      ws[addr].s = {
        alignment: {
          horizontal: isNumeric ? "right" : "left",

          vertical: "center",
        },

        border: {
          top: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },

          bottom: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },

          left: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },

          right: {
            style: "thin",
            color: {
              rgb: "EEEEEE",
            },
          },
        },
      };

      if ([3, 4, 5, 7, 8].includes(c)) {
        ws[addr].z = "#,##0.00";
      } else if (c === 6) {
        ws[addr].z = "#,##0";
      }
    }
  }

  const totalRowIndex = excelRows.length;

  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({
      r: totalRowIndex,
      c,
    });

    if (!ws[addr]) {
      continue;
    }

    ws[addr].s = {
      ...(ws[addr].s || {}),

      font: {
        bold: true,
        color: {
          rgb: "3F2B00",
        },
      },

      fill: {
        fgColor: {
          rgb: "E9C76A",
        },
      },

      alignment: {
        horizontal: c >= 3 ? "right" : "center",

        vertical: "center",
      },

      border: {
        top: {
          style: "medium",
          color: {
            rgb: "8B6508",
          },
        },

        bottom: {
          style: "thin",
          color: {
            rgb: "8B6508",
          },
        },

        left: {
          style: "thin",
          color: {
            rgb: "B8860B",
          },
        },

        right: {
          style: "thin",
          color: {
            rgb: "B8860B",
          },
        },
      },
    };
  }

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "Stok İşlemleri");

  XLSX.writeFile(
    wb,
    `Stok_Islemleri_Iscilik_Raporu_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`
  );
};

const printStokIslemRaporPdf = (rows) => {
  const totals = calculateStokIslemTotals(rows);

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />

        <title>
          Stok İşlemleri İşçilik Raporu
        </title>

        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 24px;
          }

          h1 {
            margin: 0 0 6px 0;
            font-size: 22px;
          }

          .sub {
            margin: 0 0 18px 0;
            font-size: 12px;
            color: #444;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }

          th,
          td {
            border: 1px solid #ddd;
            padding: 7px;
          }

          th {
            background: #b8860b;
            color: #fff;
            text-align: center;
            white-space: nowrap;
          }

          td.num {
            text-align: right;
            white-space: nowrap;
          }

          tr:nth-child(even) td {
            background: #fffaf0;
          }

          tr.total-row td {
            background: linear-gradient(
              90deg,
              #f7df91,
              #b8860b
            ) !important;

            color: #3f2b00;
            font-weight: 700;
            border-top: 2px solid #8b6508;
          }

          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>

      <body>
        <h1>
          Stok İşlemleri İşçilik Raporu
        </h1>

        <div class="sub">
          İşçilik Maliyet değeri 0'dan büyük Altın Verildi ve İşçilik (İade) değeri 0'dan büyük Altın Alındı işlemleri •
          Oluşturulma:
          ${new Date().toLocaleString("tr-TR")}
        </div>

        <table>
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Hesap Adı</th>
              <th>İşlem Türü</th>
              <th>Verilen İşçilik</th>
              <th>Alınan İşçilik</th>
              <th>Miktar</th>
              <th>Adet</th>
              <th>İşçilik Gr</th>
              <th>Kâr Gr</th>
            </tr>
          </thead>

          <tbody>
            ${(rows || [])
              .map(
                (r) => `
                  <tr>
                    <td>
                      ${escapeHtml(formatDateTimeTR(r.tarih))}
                    </td>

                    <td>
                      ${escapeHtml(r.hesapAdi || "")}
                    </td>

                    <td>
                      ${escapeHtml(
                        r.islemTurLabel ||
                          (r.islemTur === "ALTIN_ALINDI"
                            ? "Altın Alındı"
                            : "Altın Verildi")
                      )}
                    </td>

                    <td class="num">
                      ${escapeHtml(fmtNum(r.alinanIscilik, 2))}
                    </td>

                    <td class="num">
                      ${escapeHtml(fmtNum(r.verilenIscilik, 2))}
                    </td>

                    <td class="num">
                      ${escapeHtml(fmtNum(r.miktar, 2))}
                    </td>

                    <td class="num">
                      ${r.adet == null ? "" : escapeHtml(fmtInt(r.adet))}
                    </td>

                    <td class="num">
                      ${escapeHtml(fmtNum(r.iscilikGr, 2))}
                    </td>

                    <td class="num">
                      ${escapeHtml(fmtNum(r.kar, 2))}
                    </td>
                  </tr>
                `
              )
              .join("")}

            <tr class="total-row">
              <td>TOPLAM</td>
              <td></td>
              <td></td>

              <td class="num">
                ${escapeHtml(fmtNum(totals.alinanIscilik, 2))}
              </td>

              <td class="num">
                ${escapeHtml(fmtNum(totals.verilenIscilik, 2))}
              </td>

              <td class="num">
                ${escapeHtml(fmtNum(totals.miktar, 2))}
              </td>

              <td class="num">
                ${escapeHtml(fmtInt(totals.adet))}
              </td>

              <td class="num">
                ${escapeHtml(fmtNum(totals.iscilikGr, 2))}
              </td>

              <td class="num">
                ${escapeHtml(fmtNum(totals.kar, 2))}
              </td>
            </tr>
          </tbody>
        </table>

        <script>
          window.onload = () => {
            setTimeout(() => {
              window.focus();
              window.print();
            }, 50);
          };
        </script>
      </body>
    </html>
  `;

  const w = window.open("about:blank", "_blank");

  if (!w) {
    alert(
      "Tarayıcı pop-up penceresini engelledi. PDF yazdırmak için siteye pop-up izni verin."
    );

    return;
  }

  w.document.open();

  w.document.write(html);

  w.document.close();
};

export default function KuyumcuRaporlarPage() {
  const navigate = useNavigate();

  const userId = getUserId();

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  useEffect(() => {
    if (!userId || !token) {
      navigate("/login");
    }
  }, [userId, token, navigate]);

  /* =========================
     PANEL STATE
  ========================= */
  const [expanded, setExpanded] = useState(false);

  const handleAccordion = (panel) => (_, isExpanded) =>
    setExpanded(isExpanded ? panel : false);

  /* =========================
     BÖLGE KOD LİSTESİ
  ========================= */
  const [bolgeKodList, setBolgeKodList] = useState([]);

  const fetchBolgeKodList = useCallback(async () => {
    try {
      const resp = await axiosInstance.get("/parametreler/bolge-kod");

      setBolgeKodList(resp.data || []);
    } catch {
      setBolgeKodList([]);
    }
  }, []);

  /* =========================================================
     ORTAK: HESAPLAR (map)
  ========================================================= */
  const [hesapList, setHesapList] = useState([]);

  const [hesapMap, setHesapMap] = useState({});

  const loadHesaplar = useCallback(async () => {
    try {
      const resp = await axiosInstance.get("/hesaplar");

      const list = resp.data || [];

      setHesapList(list);

      const map = {};

      list.forEach((h) => {
        map[h.id] = h;
      });

      setHesapMap(map);
    } catch (e) {
      console.error(e);

      setHesapList([]);

      setHesapMap({});
    }
  }, []);

  /* =========================================================
     PANEL-1: HAM VERİ (Hesap bakiyeleri)
  ========================================================= */
  const [rawRows1, setRawRows1] = useState([]);

  const [loading1, setLoading1] = useState(false);

  const fetchCariBakiye = useCallback(async () => {
    setLoading1(true);

    try {
      const resp = await axiosInstance.get("/hesaplar");

      const mapped = (resp.data || []).map((d) => ({
        id: d.id,

        hesapAdi: fixMojibakeTR(d.hesapAdi),

        bolgeKod: fixMojibakeTR(d.bolgeKod || ""),

        ozelKod: fixMojibakeTR(d.ozelKod || ""),

        grupKod: d.grupKod,

        bolgeKodId: d.bolgeKodId,

        gramBakiye: Number(d.gramBakiye || 0),

        tlBakiye: Number(d.tlBakiye || 0),

        usdBakiye: Number(d.usdBakiye || 0),

        eurBakiye: Number(d.eurBakiye || 0),

        yeniAta: Number(d.yeniAta || 0),

        yeniYarim: Number(d.yeniYarim || 0),

        yeniCeyrek: Number(d.yeniCeyrek || 0),

        eskiAta: Number(d.eskiAta || 0),

        eskiYarim: Number(d.eskiYarim || 0),

        eskiCeyrek: Number(d.eskiCeyrek || 0),
      }));

      setRawRows1(mapped);
    } catch (e) {
      console.error(e);

      setRawRows1([]);

      alert("Cari bakiye rapor verileri alınamadı.");
    } finally {
      setLoading1(false);
    }
  }, []);

  /* =========================================================
     PANEL-2: HAM VERİ (Tüm hesap işlemleri)
  ========================================================= */
  const [rawRows2, setRawRows2] = useState([]);

  const [loading2, setLoading2] = useState(false);

  /* =========================================================
     PANEL-3: KASA ÖZETİ
  ========================================================= */
  const [kasaOzet3, setKasaOzet3] = useState(null);

  const [loading3, setLoading3] = useState(false);

  /* =========================================================
     PANEL-4: STOK İŞLEMLERİ İŞÇİLİK RAPORU
     - Alınan işçilik değeri 0'dan büyük ALTIN_VERILDI child satırları
     - Verilen işçilik değeri 0'dan büyük ALTIN_ALINDI child satırları
  ========================================================= */
  const [stokList4, setStokList4] = useState([]);

  const [rawRows4, setRawRows4] = useState([]);

  const [loading4, setLoading4] = useState(false);

  const [filterStart4, setFilterStart4] = useState(todayYMD());

  const [filterEnd4, setFilterEnd4] = useState(todayYMD());

  const [selectedHesapId4, setSelectedHesapId4] = useState("");

  const [selectedStokId4, setSelectedStokId4] = useState("");

  const loadStokList4 = useCallback(async () => {
    try {
      const resp = await axiosInstance.get("/stoklar");

      const list = (resp.data || [])
        .filter((s) => !s.sabitMi)
        .map((s) => ({
          ...s,

          stokAdi: fixMojibakeTR(s.stokAdi || ""),
        }))
        .sort((a, b) =>
          String(a.stokAdi || "").localeCompare(String(b.stokAdi || ""), "tr")
        );

      setStokList4(list);
    } catch (e) {
      console.error(e);

      setStokList4([]);
    }
  }, []);

  const fetchStokIslemRapor4 = useCallback(async () => {
    if (
      filterStart4 &&
      filterEnd4 &&
      toStartOfDay(filterStart4) > toEndOfDay(filterEnd4)
    ) {
      setRawRows4([]);

      return;
    }

    setLoading4(true);

    try {
      const params = {
        startDate: filterStart4,

        endDate: filterEnd4,
      };

      if (selectedHesapId4) {
        params.hesapId = Number(selectedHesapId4);
      }

      if (selectedStokId4) {
        params.stokId = Number(selectedStokId4);
      }

      const resp = await axiosInstance.get("/islemler/rapor/stok-islemleri", {
        params,
      });

      const mapped = (resp.data || []).map((r) => {
        const miktar = Number(r.miktar ?? 0);

        const adet = r.adet == null ? null : Number(r.adet);

        const alinanIscilik = Number(r.alinanIscilik ?? 0);

        const verilenIscilik = Number(r.verilenIscilik ?? 0);

        /*
         * İşçilik Gr ve Kâr frontend tarafında tekrar hesaplanmaz.
         * Hesaplama StokIslemRaporDto içerisinde yapıldığı için
         * backend tarafından dönen değerler doğrudan kullanılır.
         */
        const iscilikGr = Number(r.iscilikGr ?? 0);

        const kar = Number(r.kar ?? 0);

        return {
          ...r,

          id: r.id,

          hesapAdi: fixMojibakeTR(r.hesapAdi || ""),

          stokAdi: fixMojibakeTR(r.stokAdi || ""),

          islemTurLabel: fixMojibakeTR(
            r.islemTurLabel ||
              (r.islemTur === "ALTIN_ALINDI" ? "Altın Alındı" : "Altın Verildi")
          ),

          alinanIscilik: Number.isFinite(alinanIscilik) ? alinanIscilik : 0,

          verilenIscilik: Number.isFinite(verilenIscilik) ? verilenIscilik : 0,

          miktar: Number.isFinite(miktar) ? miktar : 0,

          adet: Number.isFinite(adet) ? adet : null,

          iscilikGr: Number.isFinite(iscilikGr) ? iscilikGr : 0,

          kar: Number.isFinite(kar) ? kar : 0,
        };
      });

      setRawRows4(mapped);
    } catch (e) {
      console.error(e);

      setRawRows4([]);

      alert("Stok işlemleri işçilik raporu alınamadı.");
    } finally {
      setLoading4(false);
    }
  }, [filterStart4, filterEnd4, selectedHesapId4, selectedStokId4]);

  const loadAllIslemler = useCallback(async () => {
    setLoading2(true);

    try {
      let list = hesapList;

      if (!list || list.length === 0) {
        const respH = await axiosInstance.get("/hesaplar");

        list = respH.data || [];
      }

      const calls = list.map((h) =>
        axiosInstance
          .get(`/islemler?hesapId=${h.id}`)
          .then((r) => ({
            hesap: h,
            items: r.data || [],
          }))
          .catch(() => ({
            hesap: h,
            items: [],
          }))
      );

      const results = await Promise.all(calls);

      const flat = [];

      results.forEach(({ hesap, items }) => {
        items.forEach((it) => {
          const birimDisplay =
            it.islemTur === "NAKIT_ISLEM_GRAM_ALINDI" ||
            it.islemTur === "NAKIT_ISLEM_GRAM_VERILDI"
              ? "GRAM"
              : it.birim;

          const islemTurText = formatEnum
            ? formatEnum(it.islemTur)
            : it.islemTur;

          flat.push({
            id: it.id,

            hesapId: hesap.id,

            hesapAdi: fixMojibakeTR(hesap.hesapAdi),

            bolgeKod: fixMojibakeTR(hesap.bolgeKod || ""),

            ozelKod: fixMojibakeTR(hesap.ozelKod || ""),

            aciklama: fixMojibakeTR(it.aciklama || ""),

            grupKod: hesap.grupKod,

            bolgeKodId: hesap.bolgeKodId,

            tarih: it.tarih,

            islemTur: it.islemTur,

            islemTurText,

            borc: Number(it.borc || 0),

            alacak: Number(it.alacak || 0),

            birim: it.birim,

            birimDisplay,
          });
        });
      });

      flat.sort(
        (a, b) => new Date(b.tarih).getTime() - new Date(a.tarih).getTime()
      );

      setRawRows2(flat);
    } catch (e) {
      console.error(e);

      setRawRows2([]);

      alert("Cari işlem rapor verileri alınamadı.");
    } finally {
      setLoading2(false);
    }
  }, [hesapList]);

  const fetchGunlukKasaOzet3 = useCallback(async () => {
    setLoading3(true);

    try {
      const resp = await axiosInstance.get(`/hesaplar/gunluk-kasa-ozet-detay`);

      setKasaOzet3(resp.data || null);
    } catch (e) {
      console.error(e);

      setKasaOzet3(null);

      alert("Kasa özeti verisi alınamadı.");
    } finally {
      setLoading3(false);
    }
  }, [userId]);

  /* =========================
     INITIAL LOAD
  ========================= */
  useEffect(() => {
    fetchBolgeKodList();

    loadHesaplar();

    loadStokList4();

    fetchCariBakiye();
  }, [fetchBolgeKodList, loadHesaplar, loadStokList4, fetchCariBakiye]);

  useEffect(() => {
    if (expanded === "panel2" && rawRows2.length === 0 && !loading2) {
      loadAllIslemler();
    }
  }, [expanded, rawRows2.length, loading2, loadAllIslemler]);

  useEffect(() => {
    if (expanded === "panel3" && !kasaOzet3 && !loading3) {
      fetchGunlukKasaOzet3();
    }
  }, [expanded, kasaOzet3, loading3, fetchGunlukKasaOzet3]);

  useEffect(() => {
    if (expanded === "panel4") {
      fetchStokIslemRapor4();
    }
  }, [expanded, fetchStokIslemRapor4]);

  /* =========================================================
     PANEL-1 FİLTRELER
  ========================================================= */
  const [selectedGroupIds1, setSelectedGroupIds1] = useState(
    GRUP_KODLARI.map((x) => x.id)
  );

  const [selectedBolgeKodId1, setSelectedBolgeKodId1] = useState("");

  const [ozelKod1, setOzelKod1] = useState("");

  const [bakiyeDurum, setBakiyeDurum] = useState("TUMU");

  const toggleGroup1 = (id) => {
    setSelectedGroupIds1((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllGroups1 = () =>
    setSelectedGroupIds1(GRUP_KODLARI.map((x) => x.id));

  const clearAllGroups1 = () => setSelectedGroupIds1([]);

  const filteredRows1 = useMemo(() => {
    const sOzel = (ozelKod1 || "").trim().toLowerCase();

    return (rawRows1 || []).filter((r) => {
      if (
        selectedGroupIds1.length > 0 &&
        !selectedGroupIds1.includes(Number(r.grupKod))
      ) {
        return false;
      }

      if (selectedBolgeKodId1) {
        if (String(r.bolgeKodId || "") !== String(selectedBolgeKodId1)) {
          return false;
        }
      }

      if (sOzel) {
        const val = String(r.ozelKod || "").toLowerCase();

        if (!val.includes(sOzel)) {
          return false;
        }
      }

      const f = getBakiyeFlags(r);

      if (bakiyeDurum === "BAKIYESIZ") {
        return !f.anyNonZero;
      }

      if (bakiyeDurum === "BAKIYELI") {
        return f.anyNonZero;
      }

      if (bakiyeDurum === "BORCLU") {
        return f.anyNegative;
      }

      if (bakiyeDurum === "ALACAKLI") {
        return f.anyPositive;
      }

      return true;
    });
  }, [rawRows1, selectedGroupIds1, selectedBolgeKodId1, ozelKod1, bakiyeDurum]);

  const cariBakiyeTotals = useMemo(
    () => calculateCariBakiyeTotals(filteredRows1),
    [filteredRows1]
  );

  const cariBakiyeGridRows = useMemo(() => {
    if (filteredRows1.length === 0) {
      return [];
    }

    return [
      ...filteredRows1,
      {
        id: "__CARI_BAKIYE_TOPLAM__",

        __totalRow: true,

        __totalRowType: "bakiye",

        hesapAdi: "TOPLAM",

        bolgeKod: "",

        ozelKod: "",

        ...cariBakiyeTotals,
      },
    ];
  }, [filteredRows1, cariBakiyeTotals]);

  const columnsCariBakiye = useMemo(
    () =>
      [
        {
          field: "hesapAdi",

          headerName: "Hesap Adı",

          flex: 1,

          minWidth: 180,
        },

        {
          field: "bolgeKod",

          headerName: "Bölge Kod",

          width: 120,
        },

        {
          field: "ozelKod",

          headerName: "Özel Kod",

          width: 120,
        },

        {
          field: "gramBakiye",

          headerName: "Gram Bakiye",

          width: 130,

          renderCell: (p) => fmtGram(p.row.gramBakiye),
        },

        {
          field: "tlBakiye",

          headerName: "TL Bakiye",

          width: 120,

          renderCell: (p) => fmtNum(p.row.tlBakiye, 2),
        },

        {
          field: "usdBakiye",

          headerName: "USD Bakiye",

          width: 120,

          renderCell: (p) => fmtNum(p.row.usdBakiye, 2),
        },

        {
          field: "eurBakiye",

          headerName: "EUR Bakiye",

          width: 120,

          renderCell: (p) => fmtNum(p.row.eurBakiye, 2),
        },

        {
          field: "yeniAta",

          headerName: "Y. Ata",

          width: 95,

          renderCell: (p) => fmtInt(p.row.yeniAta),
        },

        {
          field: "yeniYarim",

          headerName: "Y. Yarım",

          width: 95,

          renderCell: (p) => fmtInt(p.row.yeniYarim),
        },

        {
          field: "yeniCeyrek",

          headerName: "Y. Çeyrek",

          width: 95,

          renderCell: (p) => fmtInt(p.row.yeniCeyrek),
        },

        {
          field: "eskiAta",

          headerName: "E. Ata",

          width: 95,

          renderCell: (p) => fmtInt(p.row.eskiAta),
        },

        {
          field: "eskiYarim",

          headerName: "E. Yarım",

          width: 95,

          renderCell: (p) => fmtInt(p.row.eskiYarim),
        },

        {
          field: "eskiCeyrek",

          headerName: "E. Çeyrek",

          width: 95,

          renderCell: (p) => fmtInt(p.row.eskiCeyrek),
        },
      ].map((column) => ({
        ...column,

        sortable: false,

        cellClassName: getRaporTotalCellClassName,
      })),
    []
  );

  /* =========================================================
     PANEL-2 FİLTRELER
  ========================================================= */
  const [selectedGroupIds2, setSelectedGroupIds2] = useState(
    GRUP_KODLARI.map((x) => x.id)
  );

  const [filterStart2, setFilterStart2] = useState(todayYMD());

  const [filterEnd2, setFilterEnd2] = useState(todayYMD());

  const [paraBirimi2, setParaBirimi2] = useState("");

  const [selectedBolgeKodId2, setSelectedBolgeKodId2] = useState("");

  const [ozelKod2, setOzelKod2] = useState("");

  const [aciklama2, setAciklama2] = useState("");

  const [selectedIslemTipleri, setSelectedIslemTipleri] = useState(
    ISLEM_TIPI_OPTIONS.map((x) => x.key)
  );

  const toggleGroup2 = (id) => {
    setSelectedGroupIds2((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllGroups2 = () =>
    setSelectedGroupIds2(GRUP_KODLARI.map((x) => x.id));

  const clearAllGroups2 = () => setSelectedGroupIds2([]);

  const selectAllIslemTip = () =>
    setSelectedIslemTipleri(ISLEM_TIPI_OPTIONS.map((x) => x.key));

  const clearAllIslemTip = () => setSelectedIslemTipleri([]);

  const toggleIslemTip = (key) => {
    setSelectedIslemTipleri((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const filteredRows2 = useMemo(() => {
    const sOzel = (ozelKod2 || "").trim().toLowerCase();

    const sAck = (aciklama2 || "").trim().toLowerCase();

    const start = filterStart2 ? toStartOfDay(filterStart2) : null;

    const end = filterEnd2 ? toEndOfDay(filterEnd2) : null;

    return (rawRows2 || []).filter((r) => {
      if (
        selectedGroupIds2.length > 0 &&
        !selectedGroupIds2.includes(Number(r.grupKod))
      ) {
        return false;
      }

      if (start || end) {
        const t = r?.tarih ? new Date(r.tarih) : null;

        if (!t || Number.isNaN(t.getTime())) {
          return false;
        }

        if (start && t < start) {
          return false;
        }

        if (end && t > end) {
          return false;
        }
      }

      if (paraBirimi2) {
        const b = String(r.birimDisplay || r.birim || "");

        if (b !== paraBirimi2) {
          return false;
        }
      }

      if (selectedBolgeKodId2) {
        if (String(r.bolgeKodId || "") !== String(selectedBolgeKodId2)) {
          return false;
        }
      }

      if (sOzel) {
        const val = String(r.ozelKod || "").toLowerCase();

        if (!val.includes(sOzel)) {
          return false;
        }
      }

      if (sAck) {
        const val = String(r.aciklama || "").toLowerCase();

        if (!val.includes(sAck)) {
          return false;
        }
      }

      if (
        selectedIslemTipleri.length > 0 &&
        !selectedIslemTipleri.includes(String(r.islemTur))
      ) {
        return false;
      }

      return true;
    });
  }, [
    rawRows2,
    selectedGroupIds2,
    filterStart2,
    filterEnd2,
    paraBirimi2,
    selectedBolgeKodId2,
    ozelKod2,
    aciklama2,
    selectedIslemTipleri,
  ]);

  const cariIslemTotals = useMemo(
    () => calculateCariIslemTotalsByUnit(filteredRows2),
    [filteredRows2]
  );

  const cariIslemGridRows = useMemo(() => {
    if (filteredRows2.length === 0) {
      return [];
    }

    const totalRows = getOrderedIslemBirimleri(cariIslemTotals).map((unit) => ({
      id: `__CARI_ISLEM_TOPLAM_${unit}__`,

      __totalRow: true,

      __totalRowType: "islem",

      hesapAdi: "TOPLAM",

      bolgeKod: "",

      ozelKod: "",

      tarih: `${filteredRows2.length} kayıt`,

      islemTur: "",

      islemTurText: "",

      borc: cariIslemTotals[unit]?.borc || 0,

      alacak: cariIslemTotals[unit]?.alacak || 0,

      birim: unit,

      birimDisplay: unit,

      aciklama: "",
    }));

    return [...filteredRows2, ...totalRows];
  }, [filteredRows2, cariIslemTotals]);

  /* =========================================================
     PANEL-2 COLUMNS
  ========================================================= */
  const formatSmart = (val, birim) => formatValueByUnit(val, birim);

  const columnsCariIslem = useMemo(
    () =>
      [
        {
          field: "hesapAdi",

          headerName: "Hesap",

          flex: 1,

          minWidth: 160,
        },

        {
          field: "bolgeKod",

          headerName: "Bölge",

          width: 110,
        },

        {
          field: "ozelKod",

          headerName: "Özel",

          width: 110,
        },

        {
          field: "tarih",

          headerName: "Tarih",

          minWidth: 150,

          flex: 1,

          renderCell: (p) =>
            p.row.__totalRow
              ? p.row.tarih || ""
              : formatDateTimeTR(p.row.tarih),

          headerAlign: "center",

          align: "center",
        },

        {
          field: "islemTur",

          headerName: "Tür",

          minWidth: 140,

          flex: 1,

          renderCell: (p) => p.row.islemTurText || p.row.islemTur || "",

          headerAlign: "center",

          align: "center",
        },

        {
          field: "borc",

          headerName: "Verilen",

          minWidth: 120,

          flex: 1,

          renderCell: (p) => formatSmart(p.row.borc, p.row.birimDisplay),

          headerAlign: "center",

          align: "center",
        },

        {
          field: "alacak",

          headerName: "Alınan",

          minWidth: 120,

          flex: 1,

          renderCell: (p) => formatSmart(p.row.alacak, p.row.birimDisplay),

          headerAlign: "center",

          align: "center",
        },

        {
          field: "birimDisplay",

          headerName: "Birim",

          minWidth: 70,

          maxWidth: 80,

          flex: 1,

          headerAlign: "center",

          align: "center",
        },

        {
          field: "aciklama",

          headerName: "Açıklama",

          flex: 2,

          minWidth: 220,

          headerAlign: "center",

          align: "center",
        },
      ].map((column) => ({
        ...column,

        sortable: false,

        cellClassName: getRaporTotalCellClassName,
      })),
    []
  );

  const stokIslemTotals4 = useMemo(
    () => calculateStokIslemTotals(rawRows4),
    [rawRows4]
  );

  const stokIslemGridRows4 = useMemo(() => {
    if (rawRows4.length === 0) {
      return [];
    }

    return [
      ...rawRows4,

      {
        id: "__STOK_ISLEM_TOPLAM__",

        __totalRow: true,

        __totalRowType: "stok",

        tarih: "TOPLAM",

        hesapAdi: "",

        islemTurLabel: "",

        ...stokIslemTotals4,
      },
    ];
  }, [rawRows4, stokIslemTotals4]);

  /* =========================================================
     PANEL-4: STOK İŞLEMLERİ İŞÇİLİK RAPORU KOLONLARI
  ========================================================= */
  const columnsStokIslem4 = useMemo(
    () =>
      [
        {
          field: "tarih",

          headerName: "Tarih",

          minWidth: 165,

          flex: 1,

          renderCell: (p) =>
            p.row.__totalRow ? "TOPLAM" : formatDateTimeTR(p.row.tarih),

          headerAlign: "center",

          align: "center",

          sortable: false,
        },

        {
          field: "hesapAdi",

          headerName: "Hesap Adı",

          minWidth: 190,

          flex: 1.3,

          headerAlign: "center",

          align: "center",

          sortable: false,
        },

        {
          field: "islemTurLabel",

          headerName: "İşlem Türü",

          minWidth: 145,

          flex: 1,

          renderCell: (p) =>
            p.row.__totalRow
              ? ""
              : p.row.islemTurLabel || formatEnum(p.row.islemTur),

          headerAlign: "center",

          align: "center",

          sortable: false,
        },

        {
          field: "alinanIscilik",

          headerName: "Verilen İşçilik",

          minWidth: 145,

          flex: 1,

          renderCell: (p) => fmtNum(p.row.alinanIscilik, 2),

          headerAlign: "center",

          align: "center",

          sortable: false,
        },

        {
          field: "verilenIscilik",

          headerName: "Alınan İşçilik",

          minWidth: 145,

          flex: 1,

          renderCell: (p) => fmtNum(p.row.verilenIscilik, 2),

          headerAlign: "center",

          align: "center",

          sortable: false,
        },

        {
          field: "miktar",

          headerName: "Miktar",

          minWidth: 110,

          flex: 0.8,

          renderCell: (p) => fmtNum(p.row.miktar, 2),

          headerAlign: "center",

          align: "center",

          sortable: false,
        },

        {
          field: "adet",

          headerName: "Adet",

          minWidth: 90,

          flex: 0.7,

          renderCell: (p) => (p.row.adet == null ? "-" : fmtInt(p.row.adet)),

          headerAlign: "center",

          align: "center",

          sortable: false,
        },

        {
          field: "iscilikGr",

          headerName: "İşçilik Gr",

          minWidth: 155,

          flex: 1,

          renderCell: (p) => fmtNum(p.row.iscilikGr, 2),

          headerAlign: "center",

          align: "center",

          sortable: false,
        },

        {
          field: "kar",

          headerName: "Kâr",

          minWidth: 125,

          flex: 0.9,

          renderCell: (p) => fmtNum(p.row.kar, 2),

          headerAlign: "center",

          align: "center",

          sortable: false,
        },
      ].map((column) => ({
        ...column,

        cellClassName: getRaporTotalCellClassName,
      })),
    []
  );

  /* =========================================================
     PANEL-3: UI MODEL
  ========================================================= */
  const KASA_COLS = [
    {
      key: "gram",
      label: "Gram",
    },

    {
      key: "tl",
      label: "TL",
    },

    {
      key: "usd",
      label: "USD",
    },

    {
      key: "euro",
      label: "EURO",
    },

    {
      key: "yeniAta",
      label: "Yeni Ata",
    },

    {
      key: "yeniYrm",
      label: "Yeni Yrm",
    },

    {
      key: "yeniCyr",
      label: "Yeni Çyr",
    },

    {
      key: "eskiAta",
      label: "Eski Ata",
    },

    {
      key: "eskiYrm",
      label: "Eski Yrm",
    },

    {
      key: "eskiCyr",
      label: "Eski Çyr",
    },
  ];

  const ALTIN_KASA_COLS = [
    {
      key: "gram",
      label: "Gram",
    },
  ];

  const NAKIT_KASA_COLS = [
    {
      key: "tl",
      label: "TL",
    },

    {
      key: "usd",
      label: "USD",
    },

    {
      key: "euro",
      label: "EURO",
    },
  ];

  const ZIYNET_KASA_COLS = [
    {
      key: "yeniAta",
      label: "Yeni Ata",
    },

    {
      key: "yeniYrm",
      label: "Yeni Yrm",
    },

    {
      key: "yeniCyr",
      label: "Yeni Cyr",
    },

    {
      key: "eskiAta",
      label: "Eski Ata",
    },

    {
      key: "eskiYrm",
      label: "Eski Yrm",
    },

    {
      key: "eskiCyr",
      label: "Eski Cyr",
    },
  ];

  const getKasaVal = (rowKey, colKey, dto) => {
    if (!dto) {
      return 0;
    }

    const map = {
      gram: {
        borc: "borcGram",
        alacak: "alacakGram",
        fark: "farkGram",
      },

      tl: {
        borc: "borcTl",
        alacak: "alacakTl",
        fark: "farkTl",
      },

      usd: {
        borc: "borcUsd",
        alacak: "alacakUsd",
        fark: "farkUsd",
      },

      euro: {
        borc: "borcEuro",
        alacak: "alacakEuro",
        fark: "farkEuro",
      },

      yeniAta: {
        borc: "borcYeniAta",

        alacak: "alacakYeniAta",

        fark: "farkYeniAta",
      },

      yeniYrm: {
        borc: "borcYeniYrm",

        alacak: "alacakYeniYrm",

        fark: "farkYeniYrm",
      },

      yeniCyr: {
        borc: "borcYeniCyr",

        alacak: "alacakYeniCyr",

        fark: "farkYeniCyr",
      },

      eskiAta: {
        borc: "borcEskiAta",

        alacak: "alacakEskiAta",

        fark: "farkEskiAta",
      },

      eskiYrm: {
        borc: "borcEskiYrm",

        alacak: "alacakEskiYrm",

        fark: "farkEskiYrm",
      },

      eskiCyr: {
        borc: "borcEskiCyr",

        alacak: "alacakEskiCyr",

        fark: "farkEskiCyr",
      },
    };

    const field = map?.[colKey]?.[rowKey];

    if (!field) {
      return 0;
    }

    const v = Number(dto[field] || 0);

    return Number.isFinite(v) ? v : 0;
  };

  const fmtKasaCell = (colKey, val) => {
    const n = Number(val || 0);

    if (!Number.isFinite(n)) {
      return "0";
    }

    const isZiynet =
      colKey === "yeniAta" ||
      colKey === "yeniYrm" ||
      colKey === "yeniCyr" ||
      colKey === "eskiAta" ||
      colKey === "eskiYrm" ||
      colKey === "eskiCyr";

    return isZiynet ? fmtInt(n) : fmtNum(n, 2);
  };

  const getNakitKasaVal = (key, dto) => {
    if (!dto) {
      return 0;
    }

    const candidates = {
      tl: ["kasaTl", "tlKasa", "nakitKasaTl", "nakitTl", "toplamTl", "farkTl"],

      usd: [
        "kasaUsd",
        "usdKasa",
        "nakitKasaUsd",
        "nakitUsd",
        "toplamUsd",
        "farkUsd",
      ],

      euro: [
        "kasaEuro",
        "euroKasa",
        "nakitKasaEuro",
        "nakitEuro",
        "toplamEuro",
        "farkEuro",
      ],
    };

    const fields = candidates[key] || [];

    for (const f of fields) {
      const v = Number(dto?.[f] ?? 0);

      if (Number.isFinite(v) && v !== 0) {
        return v;
      }
    }

    const v0 = Number(dto?.[fields[0]] ?? 0);

    return Number.isFinite(v0) ? v0 : 0;
  };

  const getZiynetKasaVal = (key, dto) => {
    if (!dto) {
      return 0;
    }

    const candidates = {
      yeniAta: ["yeniAtaKasa"],

      yeniYrm: ["yeniYrmKasa"],

      yeniCyr: ["yeniCyrKasa"],

      eskiAta: ["eskiAtaKasa"],

      eskiYrm: ["eskiYrmKasa"],

      eskiCyr: ["eskiCyrKasa"],
    };

    const fields = candidates[key] || [];

    for (const f of fields) {
      const v = Number(dto?.[f] ?? 0);

      if (Number.isFinite(v)) {
        return v;
      }
    }

    return 0;
  };

  const fmtDetayGram = (v) => {
    const n = Number(v || 0);

    if (!Number.isFinite(n)) {
      return "0";
    }

    return fmtNum(n, 2);
  };

  const KasaDetayBox = ({ title, value }) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            opacity: 0.75,
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </div>

        <div
          style={{
            minWidth: 185,
            height: 42,
            border: "1px solid rgba(0,0,0,0.10)",
            background: "linear-gradient(180deg, #ffffff 0%, #fbfbfb 100%)",
            borderRadius: 12,
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            fontWeight: 800,
            whiteSpace: "nowrap",
            boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
          }}
        >
          {value}
        </div>
      </div>
    );
  };

  const KasaMiniTable = ({ cols, rows }) => {
    return (
      <table
        style={{
          width: "35%",
          borderCollapse: "collapse",
          marginTop: 10,
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                border: "1px solid #ddd",
                padding: 5,
                textAlign: "left",
                whiteSpace: "nowrap",
                background: "#fff",
              }}
            />

            {(cols || []).map((c) => (
              <th
                key={c.key}
                style={{
                  fontSize: 14,
                  border: "1px solid #ddd",
                  padding: 5,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  background: "#B8860B",
                  color: "#fff",
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.key}>
              <td
                style={{
                  border: "1px solid #ddd",
                  padding: 5,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {r.label}
              </td>

              {(cols || []).map((c) => (
                <td
                  key={c.key}
                  style={{
                    border: "1px solid #ddd",
                    padding: 5,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r?.renderCell ? r.renderCell(c.key) : "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const exportGunlukKasaOzetExcel3 = (dto) => {
    const headers = ["", ...KASA_COLS.map((c) => c.label)];

    const makeRow = (rowKey, label) => {
      const vals = KASA_COLS.map((c) => getKasaVal(rowKey, c.key, dto));

      return [label, ...vals];
    };

    const sectionRow = (title) => [title, ""];

    const aoa = [
      headers,

      makeRow("borc", "Borçlular Toplamı"),

      makeRow("alacak", "Alacaklılar Toplamı"),

      makeRow("fark", "Fark"),

      [],

      sectionRow("ALTIN KASA"),

      ["Devir Gram", Number(dto?.devirHasGram || 0)],

      ["Stok Has Gram Toplam", Number(dto?.hasGramStokToplam || 0)],

      ["Altın Kasası (Gram)", Number(dto?.altinKasa || 0)],

      [],

      sectionRow("NAKİT KASA"),

      ["Kasa TL", Number(getNakitKasaVal("tl", dto) || 0)],

      ["Kasa USD", Number(getNakitKasaVal("usd", dto) || 0)],

      ["Kasa EURO", Number(getNakitKasaVal("euro", dto) || 0)],

      [],

      sectionRow("ZİYNET KASA"),

      ["Kasa Yeni Ata", Number(getZiynetKasaVal("yeniAta", dto) || 0)],

      ["Kasa Yeni Yarım", Number(getZiynetKasaVal("yeniYrm", dto) || 0)],

      ["Kasa Yeni Çeyrek", Number(getZiynetKasaVal("yeniCyr", dto) || 0)],

      ["Kasa Eski Ata", Number(getZiynetKasaVal("eskiAta", dto) || 0)],

      ["Kasa Eski Yarım", Number(getZiynetKasaVal("eskiYrm", dto) || 0)],

      ["Kasa Eski Çeyrek", Number(getZiynetKasaVal("eskiCyr", dto) || 0)],

      [],

      sectionRow("İŞÇİLİK"),

      ["Verilen işçilik", Number(dto?.alinanIscilikToplam || 0)],

      ["Alınan işçilik", Number(dto?.verilenIscilikToplam || 0)],

      ["İşçilik Kâr", Number(dto?.iscilikToplamFark || 0)],

      [],

      sectionRow("MASRAF / KÂR DURUMU"),

      ["Masraf", Number(dto?.masrafGramToplam || 0)],

      ["Kâr Durumu", Number(dto?.karDurumu || 0)],

      [],

      sectionRow("TOPLAM SERMAYE"),

      ["Toplam Sermaye (Gram)", Number(dto?.toplamSermaye || 0)],
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    const goldHeaderStyle = {
      font: {
        bold: true,
        color: {
          rgb: "FFFFFF",
        },
        sz: 12,
      },

      fill: {
        fgColor: {
          rgb: "B8860B",
        },
      },

      alignment: {
        horizontal: "center",
        vertical: "center",
      },

      border: {
        top: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        bottom: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        left: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },

        right: {
          style: "thin",
          color: {
            rgb: "DDDDDD",
          },
        },
      },
    };

    const lightRowStyle = {
      alignment: {
        horizontal: "left",
        vertical: "center",
      },

      border: {
        top: {
          style: "thin",
          color: {
            rgb: "EEEEEE",
          },
        },

        bottom: {
          style: "thin",
          color: {
            rgb: "EEEEEE",
          },
        },

        left: {
          style: "thin",
          color: {
            rgb: "EEEEEE",
          },
        },

        right: {
          style: "thin",
          color: {
            rgb: "EEEEEE",
          },
        },
      },
    };

    const sectionTitles = new Set([
      "ALTIN KASA",
      "NAKİT KASA",
      "ZİYNET KASA",
      "İŞÇİLİK",
      "MASRAF / KÂR DURUMU",
      "TOPLAM SERMAYE",
    ]);

    for (let r = 0; r < aoa.length; r++) {
      const a0 = (aoa[r]?.[0] ?? "").toString().trim();

      if (
        aoa[r]?.length === 0 ||
        aoa[r]?.every((x) => x === "" || x === null || x === undefined)
      ) {
        continue;
      }

      if (sectionTitles.has(a0)) {
        for (let c = 0; c < 2; c++) {
          const addr = XLSX.utils.encode_cell({
            r,
            c,
          });

          if (!ws[addr]) {
            ws[addr] = {
              t: "s",
              v: "",
            };
          }

          ws[addr].s = goldHeaderStyle;
        }

        if (!ws["!merges"]) {
          ws["!merges"] = [];
        }

        ws["!merges"].push({
          s: {
            r,
            c: 0,
          },
          e: {
            r,
            c: 1,
          },
        });

        if (!ws["!rows"]) {
          ws["!rows"] = [];
        }

        ws["!rows"][r] = {
          hpt: 20,
        };
      } else {
        for (
          let c = 0;
          c < Math.min(headers.length, (aoa[r] || []).length);
          c++
        ) {
          const addr = XLSX.utils.encode_cell({
            r,
            c,
          });

          if (!ws[addr]) {
            continue;
          }

          ws[addr].s = ws[addr].s
            ? {
                ...ws[addr].s,
                ...lightRowStyle,
              }
            : lightRowStyle;
        }
      }
    }

    ws["!cols"] = [
      {
        wch: 28,
      },

      {
        wch: 18,
      },

      ...KASA_COLS.slice(1).map(() => ({
        wch: 14,
      })),
    ];

    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({
        r: 0,
        c,
      });

      if (!ws[addr]) {
        continue;
      }

      ws[addr].s = {
        font: {
          bold: true,
          color: {
            rgb: "FFFFFF",
          },
        },

        fill: {
          fgColor: {
            rgb: "B8860B",
          },
        },

        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },

        border: {
          top: {
            style: "thin",
            color: {
              rgb: "DDDDDD",
            },
          },

          bottom: {
            style: "thin",
            color: {
              rgb: "DDDDDD",
            },
          },

          left: {
            style: "thin",
            color: {
              rgb: "DDDDDD",
            },
          },

          right: {
            style: "thin",
            color: {
              rgb: "DDDDDD",
            },
          },
        },
      };
    }

    for (let r = 1; r <= 3; r++) {
      for (let c = 0; c < headers.length; c++) {
        const addr = XLSX.utils.encode_cell({
          r,
          c,
        });

        if (!ws[addr]) {
          continue;
        }

        const isHeaderCol = c === 0;

        ws[addr].s = {
          alignment: {
            horizontal: isHeaderCol ? "left" : "right",

            vertical: "center",
          },

          border: {
            top: {
              style: "thin",
              color: {
                rgb: "EEEEEE",
              },
            },

            bottom: {
              style: "thin",
              color: {
                rgb: "EEEEEE",
              },
            },

            left: {
              style: "thin",
              color: {
                rgb: "EEEEEE",
              },
            },

            right: {
              style: "thin",
              color: {
                rgb: "EEEEEE",
              },
            },
          },
        };

        if (!isHeaderCol) {
          const colKey = KASA_COLS[c - 1]?.key;

          const isZiynet =
            colKey === "yeniAta" ||
            colKey === "yeniYrm" ||
            colKey === "yeniCyr" ||
            colKey === "eskiAta" ||
            colKey === "eskiYrm" ||
            colKey === "eskiCyr";

          ws[addr].z = isZiynet ? "#,##0" : "#,##0.00";
        }
      }
    }

    for (let r = 4; r < aoa.length; r++) {
      const row = aoa[r] || [];

      const first = (row?.[0] ?? "").toString().trim();

      const isEmptyRow =
        row.length === 0 ||
        row.every((x) => x === "" || x === null || x === undefined);

      if (isEmptyRow) {
        continue;
      }

      if (sectionTitles.has(first)) {
        continue;
      }

      for (let c = 0; c < Math.min(2, headers.length); c++) {
        const addr = XLSX.utils.encode_cell({
          r,
          c,
        });

        if (!ws[addr]) {
          continue;
        }

        ws[addr].s = {
          ...lightRowStyle,

          alignment: {
            horizontal: c === 0 ? "left" : "right",

            vertical: "center",
          },
        };

        if (c === 1) {
          ws[addr].z = "#,##0.00";
        }
      }
    }

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Kasa Özeti");

    const fileName = `Gunluk_Altin_Kasa_Ozet_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  const printGunlukKasaOzetPdf3 = (dto) => {
    const headers = ["", ...KASA_COLS.map((c) => c.label)];

    const body = [
      [
        "Borçlular Toplam",

        ...KASA_COLS.map((c) =>
          fmtKasaCell(c.key, getKasaVal("borc", c.key, dto))
        ),
      ],

      [
        "Alacaklilar Toplam",

        ...KASA_COLS.map((c) =>
          fmtKasaCell(c.key, getKasaVal("alacak", c.key, dto))
        ),
      ],

      [
        "Fark",

        ...KASA_COLS.map((c) =>
          fmtKasaCell(c.key, getKasaVal("fark", c.key, dto))
        ),
      ],
    ];

    const doc = new jsPDF("landscape", "pt", "a4");

    doc.setFontSize(14);

    doc.text("Kasa Özeti", 40, 40);

    doc.setFontSize(10);

    doc.text(`Tarih: ${new Date().toLocaleString("tr-TR")}`, 40, 60);

    autoTable(doc, {
      head: [headers],

      body,

      startY: 75,

      styles: {
        fontSize: 9,
        cellPadding: 6,
      },

      headStyles: {
        fillColor: [184, 134, 11],
      },

      columnStyles: {
        0: {
          halign: "left",
        },
      },
    });

    let y = doc.lastAutoTable.finalY + 18;

    doc.setFontSize(12);

    doc.text("Altın Kasa", 40, y);

    y += 8;

    autoTable(doc, {
      head: [["Kalem", "(Gram)"]],

      body: [
        ["Devir Gram", fmtDetayGram(dto?.devirHasGram || 0)],

        ["Stok Has Gram Toplam", fmtDetayGram(dto?.hasGramStokToplam || 0)],

        ["Altın Kasası (Gram)", fmtDetayGram(dto?.altinKasa || 0)],
      ],

      startY: y,

      styles: {
        fontSize: 9,
        cellPadding: 6,
      },

      headStyles: {
        fillColor: [184, 134, 11],
      },
    });

    y = doc.lastAutoTable.finalY + 18;

    doc.setFontSize(12);

    doc.text("Nakit Kasa", 40, y);

    y += 8;

    autoTable(doc, {
      head: [["Para", "Tutar"]],

      body: [
        ["TL", fmtNum(getNakitKasaVal("tl", dto), 2)],

        ["USD", fmtNum(getNakitKasaVal("usd", dto), 2)],

        ["EURO", fmtNum(getNakitKasaVal("euro", dto), 2)],
      ],

      startY: y,

      styles: {
        fontSize: 9,
        cellPadding: 6,
      },

      headStyles: {
        fillColor: [184, 134, 11],
      },
    });

    y = doc.lastAutoTable.finalY + 18;

    doc.setFontSize(12);

    doc.text("Ziynet Kasa", 40, y);

    y += 8;

    autoTable(doc, {
      head: [["Ziynet", "Adet"]],

      body: [
        ["Yeni Ata", fmtInt(getZiynetKasaVal("yeniAta", dto))],

        ["Yeni Yarım", fmtInt(getZiynetKasaVal("yeniYrm", dto))],

        ["Yeni Çeyrek", fmtInt(getZiynetKasaVal("yeniCyr", dto))],

        ["Eski Ata", fmtInt(getZiynetKasaVal("eskiAta", dto))],

        ["Eski Yarım", fmtInt(getZiynetKasaVal("eskiYrm", dto))],

        ["Eski Çeyrek", fmtInt(getZiynetKasaVal("eskiCyr", dto))],
      ],

      startY: y,

      styles: {
        fontSize: 9,
        cellPadding: 6,
      },

      headStyles: {
        fillColor: [184, 134, 11],
      },
    });

    y = doc.lastAutoTable.finalY + 18;

    doc.setFontSize(12);

    doc.text("Isçilik / Masraf / Kâr Durumu", 40, y);

    y += 8;

    autoTable(doc, {
      head: [["Kalem", "Toplam (Gram)"]],

      body: [
        ["Verilen işçilik", fmtDetayGram(dto?.alinanIscilikToplam || 0)],

        ["Alınan işçilik", fmtDetayGram(dto?.verilenIscilikToplam || 0)],

        ["İşçilik Kâr", fmtDetayGram(dto?.iscilikToplamFark || 0)],

        ["Masraf", fmtDetayGram(dto?.masrafGramToplam || 0)],

        ["Kâr Durumu", fmtDetayGram(dto?.karDurumu || 0)],
      ],

      startY: y,

      styles: {
        fontSize: 9,
        cellPadding: 6,
      },

      headStyles: {
        fillColor: [184, 134, 11],
      },
    });

    y = doc.lastAutoTable.finalY + 18;

    doc.setFontSize(12);

    doc.text("Toplam Sermaye", 40, y);

    y += 8;

    autoTable(doc, {
      head: [["Kalem", "(Gram)"]],

      body: [["Toplam Sermaye (Gram)", fmtDetayGram(dto?.toplamSermaye || 0)]],

      startY: y,

      styles: {
        fontSize: 9,
        cellPadding: 6,
      },

      headStyles: {
        fillColor: [184, 134, 11],
      },
    });

    doc.save(
      `Gunluk_Altin_Kasa_Ozet_${new Date().toISOString().slice(0, 10)}.pdf`
    );
  };

  return (
    <div className="kuyumcu-page-container">
      <PremiumBackground />

      <KuyumcuLeftSideNavbar />

      <div className="kuyumcu-page-content">
        {/* ÜST BAŞLIK */}
        <Box className="raporlar-titlebar">
          <Box>
            <Typography className="raporlar-title">
              {" "}
              <FaChartPie />
              Raporlar
            </Typography>

            <Typography className="raporlar-subtitle">
              Firma kapsamındaki tüm hesapların işlemlerinden üretilen raporlar
            </Typography>
          </Box>

          <Box className="raporlar-title-actions">
            <Chip
              label={`Bakiye: ${filteredRows1.length} | İşlem: ${filteredRows2.length} | Stok İşlem: ${rawRows4.length}`}
              className="chip-soft"
            />

            <Button
              className="btn-soft"
              variant="outlined"
              onClick={() => {
                fetchCariBakiye();

                if (expanded === "panel2") {
                  loadAllIslemler();
                }

                if (expanded === "panel3") {
                  fetchGunlukKasaOzet3();
                }

                if (expanded === "panel4") {
                  fetchStokIslemRapor4();
                }
              }}
              disabled={loading1 || loading2 || loading3 || loading4}
            >
              Yenile
            </Button>
          </Box>
        </Box>

        {/* PANEL 1: CARİ BAKİYE */}
        <Accordion
          expanded={expanded === "panel1"}
          onChange={handleAccordion("panel1")}
          className="rapor-accordion"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="accordion-head">
              <Typography className="accordion-title">
                Cari Hesap Bakiye Listesi
              </Typography>

              <Typography className="accordion-desc">
                Hesaplar ve bakiyeleri
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <div className="rapor-filter-card">
              <Typography className="filter-title">Filtreler</Typography>

              <Divider className="filter-divider" />

              <div className="filter-grid">
                <div className="filter-block">
                  <Box className="filter-block-head">
                    <Typography className="filter-block-title">
                      Cari Grup Kodları
                    </Typography>

                    <Box className="filter-mini-actions">
                      <Button
                        size="small"
                        className="mini-btn"
                        onClick={selectAllGroups1}
                      >
                        Tümü
                      </Button>

                      <Button
                        size="small"
                        className="mini-btn"
                        onClick={clearAllGroups1}
                      >
                        Temizle
                      </Button>
                    </Box>
                  </Box>

                  <FormControl component="fieldset">
                    <FormGroup className="group-checks">
                      {GRUP_KODLARI.map((g) => (
                        <FormControlLabel
                          key={g.id}
                          control={
                            <Checkbox
                              checked={selectedGroupIds1.includes(g.id)}
                              onChange={() => toggleGroup1(g.id)}
                            />
                          }
                          label={g.label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </div>

                <div className="filter-block">
                  <Typography className="filter-block-title">
                    Borç / Alacak Durumu
                  </Typography>

                  <FormControl component="fieldset">
                    <RadioGroup
                      value={bakiyeDurum}
                      onChange={(e) => setBakiyeDurum(e.target.value)}
                      className="radio-stack"
                    >
                      <FormControlLabel
                        value="TUMU"
                        control={<Radio />}
                        label="Tümü"
                      />

                      <FormControlLabel
                        value="BORCLU"
                        control={<Radio />}
                        label="Borçlu Hesaplar"
                      />

                      <FormControlLabel
                        value="ALACAKLI"
                        control={<Radio />}
                        label="Alacaklı Hesaplar"
                      />

                      <FormControlLabel
                        value="BAKIYESIZ"
                        control={<Radio />}
                        label="Bakiyesiz Hesaplar"
                      />

                      <FormControlLabel
                        value="BAKIYELI"
                        control={<Radio />}
                        label="Bakiyeli Hesaplar"
                      />
                    </RadioGroup>
                  </FormControl>
                </div>

                <div className="filter-block">
                  <Typography className="filter-block-title">
                    Diğer Filtreler
                  </Typography>

                  <div className="filter-field">
                    <TextField
                      select
                      label="Bölge Kodu"
                      value={selectedBolgeKodId1}
                      onChange={(e) => setSelectedBolgeKodId1(e.target.value)}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">Tümü</MenuItem>

                      {(bolgeKodList || []).map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.bolgeKod}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>

                  <div className="filter-field">
                    <TextField
                      label="Özel Kod"
                      value={ozelKod1}
                      onChange={(e) => setOzelKod1(e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Örn: VIP, A-01"
                    />
                  </div>

                  <div className="filter-actions">
                    <div className="filter-actions">
                      <ReportExportButtons
                        onExcel={() => exportCariBakiyeExcel(filteredRows1)}
                        onPdf={() => printCariBakiyeAsPdf(filteredRows1)}
                        excelDisabled={loading1}
                        pdfDisabled={loading1}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rapor-grid-area">
              {loading1 ? (
                <div className="loading-wrap">
                  <CircularProgress />

                  <Typography className="loading-text">
                    Yükleniyor...
                  </Typography>
                </div>
              ) : (
                <ProsetDataGrid
                  rows={cariBakiyeGridRows}
                  columns={columnsCariBakiye}
                  pageSize={25}
                />
              )}
            </div>
          </AccordionDetails>
        </Accordion>

        {/* PANEL 2: CARİ İŞLEM LİSTESİ */}
        <Accordion
          expanded={expanded === "panel2"}
          onChange={handleAccordion("panel2")}
          className="rapor-accordion"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="accordion-head">
              <Typography className="accordion-title">
                Cari İşlem Listesi
              </Typography>

              <Typography className="accordion-desc">
                Tüm hesap işlemleri (Hesap İşlemler tablosu ile aynı)
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <div className="rapor-filter-card">
              <Typography className="filter-title">Filtreler</Typography>

              <Divider className="filter-divider" />

              <div className="filter-grid-2">
                <div className="filter-block">
                  <Box className="filter-block-head">
                    <Typography className="filter-block-title">
                      Cari Grup Kodları
                    </Typography>

                    <Box className="filter-mini-actions">
                      <Button
                        size="small"
                        className="mini-btn"
                        onClick={selectAllGroups2}
                      >
                        Tümü
                      </Button>

                      <Button
                        size="small"
                        className="mini-btn"
                        onClick={clearAllGroups2}
                      >
                        Temizle
                      </Button>
                    </Box>
                  </Box>

                  <FormControl component="fieldset">
                    <FormGroup className="group-checks">
                      {GRUP_KODLARI.map((g) => (
                        <FormControlLabel
                          key={g.id}
                          control={
                            <Checkbox
                              checked={selectedGroupIds2.includes(g.id)}
                              onChange={() => toggleGroup2(g.id)}
                            />
                          }
                          label={g.label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </div>

                <div className="filter-block">
                  <Typography className="filter-block-title">
                    Detay Filtreler
                  </Typography>

                  <div className="filter-row">
                    <TextField
                      type="date"
                      label="Başlangıç"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={filterStart2}
                      onChange={(e) => setFilterStart2(e.target.value)}
                      size="small"
                      fullWidth
                    />

                    <TextField
                      type="date"
                      label="Bitiş"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={filterEnd2}
                      onChange={(e) => setFilterEnd2(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </div>

                  <div className="filter-row">
                    <TextField
                      select
                      label="Para Birimi"
                      value={paraBirimi2}
                      onChange={(e) => setParaBirimi2(e.target.value)}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="">Tümü</MenuItem>

                      <MenuItem value="TL">TL</MenuItem>

                      <MenuItem value="USD">USD</MenuItem>

                      <MenuItem value="EUR">EUR</MenuItem>

                      <MenuItem value="GRAM">GRAM</MenuItem>

                      <MenuItem value="ADET">ADET</MenuItem>
                    </TextField>

                    <TextField
                      select
                      label="Bölge Kodu"
                      value={selectedBolgeKodId2}
                      onChange={(e) => setSelectedBolgeKodId2(e.target.value)}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="">Tümü</MenuItem>

                      {(bolgeKodList || []).map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.bolgeKod}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>

                  <div className="filter-row">
                    <TextField
                      label="Özel Kod"
                      value={ozelKod2}
                      onChange={(e) => setOzelKod2(e.target.value)}
                      size="small"
                      fullWidth
                    />

                    <TextField
                      label="Açıklama"
                      value={aciklama2}
                      onChange={(e) => setAciklama2(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </div>
                </div>

                <div className="filter-block">
                  <Box className="filter-block-head">
                    <Typography className="filter-block-title">
                      İşlem Tipi
                    </Typography>

                    <Box className="filter-mini-actions">
                      <Button
                        size="small"
                        className="mini-btn"
                        onClick={selectAllIslemTip}
                      >
                        Tümü
                      </Button>

                      <Button
                        size="small"
                        className="mini-btn"
                        onClick={clearAllIslemTip}
                      >
                        Temizle
                      </Button>
                    </Box>
                  </Box>

                  <FormControl component="fieldset">
                    <FormGroup className="islem-type-checks">
                      {ISLEM_TIPI_OPTIONS.map((t) => (
                        <FormControlLabel
                          key={t.key}
                          control={
                            <Checkbox
                              checked={selectedIslemTipleri.includes(t.key)}
                              onChange={() => toggleIslemTip(t.key)}
                            />
                          }
                          label={t.label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </div>

                <div className="filter-actions">
                  <ReportExportButtons
                    onExcel={() => exportCariIslemExcel(filteredRows2)}
                    onPdf={() => printCariIslemAsPdf(filteredRows2)}
                    excelDisabled={loading2}
                    pdfDisabled={loading2}
                  />

                  <Button
                    variant="outlined"
                    className="btn-soft"
                    onClick={() => loadAllIslemler()}
                    disabled={loading2}
                  >
                    Veriyi Yenile
                  </Button>
                </div>
              </div>
            </div>

            <div className="rapor-grid-area">
              {loading2 ? (
                <div className="loading-wrap">
                  <CircularProgress />

                  <Typography className="loading-text">
                    Yükleniyor...
                  </Typography>
                </div>
              ) : (
                <ProsetDataGrid
                  rows={cariIslemGridRows}
                  columns={columnsCariIslem}
                  pageSize={25}
                />
              )}
            </div>
          </AccordionDetails>
        </Accordion>

        {/* PANEL 3: Placeholder */}
        <Accordion
          expanded={expanded === "panel3"}
          onChange={handleAccordion("panel3")}
          className="rapor-accordion"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="accordion-head">
              <Typography className="accordion-title">Kasa Özeti</Typography>

              <Typography className="accordion-desc">
                Mevcut Kasa Genel Özeti
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <div className="rapor-filter-card">
              <Box
                className="filter-block-head"
                style={{
                  marginBottom: 12,
                }}
              >
                <Box>
                  <Typography className="filter-title">Kasa Özeti</Typography>

                  <Typography className="accordion-desc">
                    Genel Kasa Özet Raporu
                  </Typography>
                </Box>

                <Box
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <ReportExportButtons
                    onExcel={() => exportGunlukKasaOzetExcel3(kasaOzet3)}
                    onPdf={() => printGunlukKasaOzetPdf3(kasaOzet3)}
                    excelDisabled={loading3 || !kasaOzet3}
                    pdfDisabled={loading3 || !kasaOzet3}
                  />

                  <Button
                    className="btn-soft"
                    variant="outlined"
                    onClick={() => fetchGunlukKasaOzet3()}
                    disabled={loading3}
                  >
                    Yenile
                  </Button>
                </Box>
              </Box>

              <Divider className="filter-divider" />

              {loading3 ? (
                <div className="loading-wrap">
                  <CircularProgress />

                  <Typography className="loading-text">
                    Yükleniyor...
                  </Typography>
                </div>
              ) : !kasaOzet3 ? (
                <div className="placeholder-card">
                  <Typography className="placeholder-title">
                    Veri yok
                  </Typography>

                  <Typography className="placeholder-text">
                    Kasa özeti alınamadı veya henüz hesap bulunmuyor.
                  </Typography>
                </div>
              ) : (
                <div
                  style={{
                    overflowX: "auto",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: 10,
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            border: "1px solid #ddd",
                            padding: 10,
                            textAlign: "left",
                            whiteSpace: "nowrap",
                          }}
                        ></th>

                        {KASA_COLS.map((c) => (
                          <th
                            key={c.key}
                            style={{
                              fontSize: 14,
                              border: "1px solid #ddd",
                              padding: 5,
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              background: "#B8860B",
                              color: "#fff",
                            }}
                          >
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {[
                        {
                          key: "borc",
                          label: "Borçlular Toplamı",
                        },

                        {
                          key: "alacak",
                          label: "Alacaklılar Toplamı",
                        },

                        {
                          key: "fark",
                          label: "Fark",
                        },
                      ].map((r) => (
                        <tr key={r.key}>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: 5,
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.label}
                          </td>

                          {KASA_COLS.map((c) => {
                            const val = getKasaVal(r.key, c.key, kasaOzet3);

                            return (
                              <td
                                key={c.key}
                                style={{
                                  border: "1px solid #ddd",
                                  padding: 5,
                                  textAlign: "right",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {fmtKasaCell(c.key, val)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <KasaMiniTable
                    cols={ALTIN_KASA_COLS}
                    rows={[
                      {
                        key: "devir",

                        label: "Devir Gram",

                        renderCell: () =>
                          fmtDetayGram(kasaOzet3?.devirHasGram || 0),
                      },

                      {
                        key: "stok",

                        label: "Stok Has Gram Toplam",

                        renderCell: () =>
                          fmtDetayGram(kasaOzet3?.hasGramStokToplam || 0),
                      },

                      {
                        key: "altinKasa",

                        label: "Altın Kasası (Gram)",

                        renderCell: () =>
                          fmtDetayGram(kasaOzet3?.altinKasa || 0),
                      },
                    ]}
                  />

                  <KasaMiniTable
                    cols={NAKIT_KASA_COLS}
                    rows={[
                      {
                        key: "nakitKasa",

                        label: "Kasa",

                        renderCell: (colKey) =>
                          fmtNum(getNakitKasaVal(colKey, kasaOzet3), 2),
                      },
                    ]}
                  />

                  <KasaMiniTable
                    cols={ZIYNET_KASA_COLS}
                    rows={[
                      {
                        key: "ziynetKasa",

                        label: "Kasa",

                        renderCell: (colKey) =>
                          fmtInt(getZiynetKasaVal(colKey, kasaOzet3)),
                      },
                    ]}
                  />

                  <div
                    style={{
                      marginTop: 18,
                      display: "flex",
                      flexDirection: "column",
                      gap: 16,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          marginTop: 56,
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 18,
                          flexWrap: "wrap",
                        }}
                      >
                        <KasaDetayBox
                          title="Verilen işçilik"
                          value={fmtDetayGram(
                            kasaOzet3?.alinanIscilikToplam || 0
                          )}
                        />

                        <KasaDetayBox
                          title="Alınan işçilik"
                          value={fmtDetayGram(
                            kasaOzet3?.verilenIscilikToplam || 0
                          )}
                        />

                        <KasaDetayBox
                          title="İşçilik Kâr"
                          value={fmtDetayGram(
                            kasaOzet3?.iscilikToplamFark || 0
                          )}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 14,
                          alignItems: "flex-end",
                        }}
                      >
                        <KasaDetayBox
                          title="Masraf"
                          value={fmtDetayGram(kasaOzet3?.masrafGramToplam || 0)}
                        />

                        <KasaDetayBox
                          title="Kâr Durumu"
                          value={fmtDetayGram(kasaOzet3?.karDurumu || 0)}
                        />

                        <div
                          style={{
                            color: "green",
                            display: "flex",
                            justifyContent: "flex-end",
                            marginBottom: 15,
                          }}
                        >
                          <KasaDetayBox
                            title="Toplam Sermaye (Gram)"
                            value={fmtDetayGram(kasaOzet3?.toplamSermaye || 0)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </AccordionDetails>
        </Accordion>

        {/* PANEL 4: STOK İŞLEMLERİ İŞÇİLİK RAPORU */}
        <Accordion
          expanded={expanded === "panel4"}
          onChange={handleAccordion("panel4")}
          className="rapor-accordion"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="accordion-head">
              <Typography className="accordion-title">
                Stok Bazlı (Kâr Raporu)
              </Typography>

              <Typography className="accordion-desc">
                İşçilik farkı bulunan Altın Verildi ve Altın Alındı işlemlerinin
                satır bazlı işçilik ve kâr raporu
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <div className="rapor-filter-card">
              <Box
                className="filter-block-head"
                style={{
                  marginBottom: 12,
                }}
              >
                <Box>
                  <Typography className="filter-title">
                    İşlem Filtreleri
                  </Typography>

                  <Typography className="accordion-desc">
                    Bu raporda İşçilik Maliyet değeri 0'dan büyük Altın Verildi
                    ve İşçilik İade değeri 0'dan büyük Altın Alındı işlemleri
                    gösterilir.
                  </Typography>
                </Box>

                <Chip
                  label={`${rawRows4.length} kayıt`}
                  className="chip-soft"
                />
              </Box>

              <Divider className="filter-divider" />

              <div className="filter-block">
                <div className="filter-row">
                  <TextField
                    type="date"
                    label="Başlangıç Tarihi"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={filterStart4}
                    onChange={(e) => setFilterStart4(e.target.value)}
                    size="small"
                    fullWidth
                  />

                  <TextField
                    type="date"
                    label="Bitiş Tarihi"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={filterEnd4}
                    onChange={(e) => setFilterEnd4(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </div>

                <div className="filter-row">
                  <TextField
                    select
                    label="Hesap"
                    value={selectedHesapId4}
                    onChange={(e) => setSelectedHesapId4(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">Tüm Hesaplar</MenuItem>

                    {(hesapList || [])
                      .slice()
                      .sort((a, b) =>
                        String(a.hesapAdi || "").localeCompare(
                          String(b.hesapAdi || ""),
                          "tr"
                        )
                      )
                      .map((h) => (
                        <MenuItem key={h.id} value={h.id}>
                          {fixMojibakeTR(h.hesapAdi || "")}
                        </MenuItem>
                      ))}
                  </TextField>

                  <TextField
                    select
                    label="Stok"
                    value={selectedStokId4}
                    onChange={(e) => setSelectedStokId4(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">Tüm Stoklar</MenuItem>

                    {(stokList4 || []).map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.stokAdi}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                <div className="filter-actions">
                  <ReportExportButtons
                    onExcel={() => exportStokIslemRaporExcel(rawRows4)}
                    onPdf={() => printStokIslemRaporPdf(rawRows4)}
                    excelDisabled={loading4 || rawRows4.length === 0}
                    pdfDisabled={loading4 || rawRows4.length === 0}
                  />

                  <Button
                    variant="outlined"
                    className="btn-soft"
                    onClick={fetchStokIslemRapor4}
                    disabled={loading4}
                  >
                    Veriyi Yenile
                  </Button>
                </div>
              </div>
            </div>

            <div className="rapor-grid-area">
              {loading4 ? (
                <div className="loading-wrap">
                  <CircularProgress />

                  <Typography className="loading-text">
                    Yükleniyor...
                  </Typography>
                </div>
              ) : (
                <ProsetDataGrid
                  rows={stokIslemGridRows4}
                  columns={columnsStokIslem4}
                  pageSize={25}
                />
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
}
